// src/features/tidsregistering/components/attendance/EditTimeEntryDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Alert,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import { format } from 'date-fns';
import StaffMember from '../../../../components/common/StaffMember';

/**
 * Props:
 *   open              — boolean
 *   onClose           — () => void
 *   onSubmit          — ({ start_time, end_time, registration_code_id }) => void
 *   onDelete          — () => void, or nothing to hide the delete button
 *   slot              — slot object from attendanceAdapter (null when closed)
 *   selectedDate      — string 'yyyy-MM-dd'
 *   registrationCodes — RegistrationCode[]
 *   loading           — boolean  (save in progress)
 *   deleteLoading     — boolean  (delete in progress)
 *   submitError       — string | null
 */
export default function EditTimeEntryDialog({
  open,
  onClose,
  onSubmit,
  onDelete,
  slot,
  selectedDate,
  registrationCodes = [],
  loading = false,
  deleteLoading = false,
  submitError = null,
}) {
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [selectedCodeId, setSelectedCodeId] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const staffMember = slot?.staffMember ?? null;

  useEffect(() => {
    if (open && slot) {
      const reg = slot.actualRegistration ?? null;
      // Use the actual registration's times, not the segment's (a single registration can
      // produce multiple display segments when it spans a shift boundary or break).
      const rawStart = reg?.start_time ?? null;  // "HH:MM:SS" from API
      setStartTime(rawStart ? rawStart.substring(0, 5) : (slot.startDate ? format(slot.startDate, 'HH:mm') : ''));
      if (slot.isLive) {
        setEndTime('');
      } else {
        const rawEnd = reg?.end_time ?? null;
        setEndTime(rawEnd ? rawEnd.substring(0, 5) : (slot.endDate ? format(slot.endDate, 'HH:mm') : ''));
      }
      setSelectedCodeId(slot.registrationCode?.registration_code_id ?? '');
      setConfirmDelete(false);
    }
  }, [open, slot]);

  const isLive = !!slot?.isLive;
  const canSubmit = !!startTime && !!selectedCodeId && !loading && !deleteLoading;
  const anyBusy = loading || deleteLoading;

  const handleSubmit = () => {
    if (!canSubmit) return;
    const payload = {
      start_time: startTime,
      registration_code_id: selectedCodeId,
    };
    if (endTime) payload.end_time = endTime;
    onSubmit(payload);
  };

  const handleDeleteClick = () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    onDelete();
  };

  const handleClose = () => {
    setConfirmDelete(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Redigér tidsregistrering</DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {staffMember && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Medarbejder
              </Typography>
              <StaffMember staffMember={staffMember} avatarSize={32} clickable={false} />
            </Box>
          )}

          <Divider />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {selectedDate}
            </Typography>
            {isLive && (
              <Chip label="Aktiv" color="success" size="small" variant="outlined" />
            )}
          </Box>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Fra"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Til"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              placeholder={isLive ? 'Ikke stemplet ud' : ''}
            />
          </Stack>

          <FormControl fullWidth>
            <InputLabel id="edit-time-entry-dialog-select-label">Registreringskode</InputLabel>
            <Select labelId="edit-time-entry-dialog-select-label"
              value={selectedCodeId}
              label="Registreringskode"
              onChange={(e) => setSelectedCodeId(e.target.value)}
              MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
            >
              {registrationCodes.map((c) => (
                <MenuItem key={c.registration_code_id} value={c.registration_code_id}>
                  <Box>
                    <Typography variant="body2">{c.title}</Typography>
                    {c.code && (
                      <Typography variant="caption" color="text.secondary">{c.code}</Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {submitError && (
            <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>
              {submitError}
            </Alert>
          )}

          {confirmDelete && (
            <Alert severity="warning">
              Bekræft sletning — klik Slet igen for at bekræfte.
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {onDelete ? (
          <Button
            onClick={handleDeleteClick}
            color={confirmDelete ? 'error' : 'inherit'}
            variant={confirmDelete ? 'contained' : 'text'}
            disabled={anyBusy}
          >
            {deleteLoading ? 'Sletter…' : confirmDelete ? 'Bekræft slet' : 'Slet'}
          </Button>
        ) : <span />}

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} disabled={anyBusy}>
            Annuller
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
            {loading ? 'Gemmer…' : 'Gem'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
